import { queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';

/**
 * Server-derived permission flags for the current recruiter. The SPA only
 * REFLECTS these — the authority is the BFF (`GET /me/permissions`, a pure JWT
 * projection). `canManageForms` (EU-454, ampliado por DEV-93 para a role
 * `specialist` além de `gestor-conta`) gates o CRUD de Forms;
 * `canManageAssessments` (só role `admin`) gate a edição da política de reuso
 * dos testes, que é GLOBAL (o Core recusa não-admin com 403 de qualquer forma).
 */
export interface MePermissionsResponse {
  canManageForms: boolean;
  canManageAssessments: boolean;
  /** EU-593 — role `rank-consultor` (sem bypass de admin): gate do "Exportar inscritos". */
  canExportRankSubscribers: boolean;
}

export class MePermissionsFetchError extends Error {
  readonly name = 'MePermissionsFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

async function fetchMePermissions(signal?: AbortSignal): Promise<MePermissionsResponse> {
  const res = await fetchClient<MePermissionsResponse>({
    method: 'GET',
    url: '/me/permissions',
    signal,
  });
  if (res.error) {
    throw new MePermissionsFetchError(
      `Failed to load permissions (status ${res.error.status})`,
      res.error.status
    );
  }
  return res.data;
}

/**
 * TanStack Query options for `GET /me/permissions`. JWT-projected on the BFF —
 * cheap, no DB touch — so a generous staleTime is fine. Fail closed: the
 * consumers treat `data === undefined` (loading or errored) as "cannot manage",
 * so a permission-fetch failure never leaks the maintenance affordances.
 */
export function mePermissionsQueryOptions() {
  return queryOptions({
    queryKey: ['recruiter', 'me', 'permissions'] as const,
    queryFn: ({ signal }) => fetchMePermissions(signal),
    staleTime: 5 * 60_000,
    retry: (failureCount, err) => {
      if (err instanceof MePermissionsFetchError && err.status >= 400 && err.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
